/**
 * Schedule type definitions
 * Synchronized with backend/internal/models/schedule.go
 */

export interface Schedule {
  id: string
  batchId: string
  sessionNumber: number
  title: string
  description?: string
  dateTime: string // RFC3339 format
  durationMinutes: number
  location?: string
  meetingLink?: string
  type: ScheduleType
  isAttendanceRequired: boolean
  createdAt: string
  updatedAt?: string
}

// Schedule types matching BE validation
export type ScheduleType = 'theory' | 'practical' | 'assessment' | 'orientation'

export const SCHEDULE_TYPES: ScheduleType[] = ['theory', 'practical', 'assessment', 'orientation']

export interface CreateScheduleInput {
  sessionNumber: number
  title: string
  description?: string
  dateTime: string // RFC3339 format
  durationMinutes: number
  location?: string
  meetingLink?: string
  type: ScheduleType
  isAttendanceRequired?: boolean
}

export interface UpdateScheduleInput {
  sessionNumber?: number
  title?: string
  description?: string
  dateTime?: string
  durationMinutes?: number
  location?: string
  meetingLink?: string
  type?: ScheduleType
  isAttendanceRequired?: boolean
}

// Type Guards
export function isSchedule(obj: unknown): obj is Schedule {
  if (!obj || typeof obj !== 'object') return false

  const schedule = obj as Record<string, unknown>

  return (
    typeof schedule.id === 'string' &&
    typeof schedule.batchId === 'string' &&
    typeof schedule.sessionNumber === 'number' &&
    schedule.sessionNumber > 0 &&
    typeof schedule.title === 'string' &&
    schedule.title.length > 0 &&
    typeof schedule.dateTime === 'string' &&
    typeof schedule.durationMinutes === 'number' &&
    schedule.durationMinutes > 0 &&
    typeof schedule.type === 'string' &&
    SCHEDULE_TYPES.includes(schedule.type as ScheduleType) &&
    typeof schedule.isAttendanceRequired === 'boolean' &&
    typeof schedule.createdAt === 'string' &&
    (schedule.description === undefined || typeof schedule.description === 'string') &&
    (schedule.location === undefined || typeof schedule.location === 'string') &&
    (schedule.meetingLink === undefined || typeof schedule.meetingLink === 'string')
  )
}

export function isCreateScheduleInput(obj: unknown): obj is CreateScheduleInput {
  if (!obj || typeof obj !== 'object') return false

  const input = obj as Record<string, unknown>

  return (
    typeof input.sessionNumber === 'number' &&
    input.sessionNumber > 0 &&
    typeof input.title === 'string' &&
    input.title.length > 0 &&
    typeof input.dateTime === 'string' &&
    typeof input.durationMinutes === 'number' &&
    input.durationMinutes > 0 &&
    typeof input.type === 'string' &&
    SCHEDULE_TYPES.includes(input.type as ScheduleType) &&
    (input.description === undefined || typeof input.description === 'string') &&
    (input.location === undefined || typeof input.location === 'string') &&
    (input.meetingLink === undefined || typeof input.meetingLink === 'string') &&
    (input.isAttendanceRequired === undefined || typeof input.isAttendanceRequired === 'boolean')
  )
}

export function isUpdateScheduleInput(obj: unknown): obj is UpdateScheduleInput {
  if (!obj || typeof obj !== 'object') return false

  const input = obj as Record<string, unknown>

  // All fields are optional, but if present, must be valid
  if (input.sessionNumber !== undefined) {
    if (typeof input.sessionNumber !== 'number' || input.sessionNumber <= 0) {
      return false
    }
  }

  if (input.title !== undefined) {
    if (typeof input.title !== 'string' || input.title.length === 0) {
      return false
    }
  }

  if (input.dateTime !== undefined && typeof input.dateTime !== 'string') {
    return false
  }

  if (input.durationMinutes !== undefined) {
    if (typeof input.durationMinutes !== 'number' || input.durationMinutes <= 0) {
      return false
    }
  }

  if (input.type !== undefined) {
    if (typeof input.type !== 'string' || !SCHEDULE_TYPES.includes(input.type as ScheduleType)) {
      return false
    }
  }

  if (input.description !== undefined && typeof input.description !== 'string') {
    return false
  }

  if (input.location !== undefined && typeof input.location !== 'string') {
    return false
  }

  if (input.meetingLink !== undefined && typeof input.meetingLink !== 'string') {
    return false
  }

  if (input.isAttendanceRequired !== undefined && typeof input.isAttendanceRequired !== 'boolean') {
    return false
  }

  return true
}

// Helper function to validate RFC3339 date format
export function isValidRFC3339(dateString: string): boolean {
  const date = new Date(dateString)
  return !isNaN(date.getTime())
}

// Helper function to format schedule date/time for display
export function formatScheduleDateTime(dateTime: string): string {
  const date = new Date(dateTime)
  return date.toLocaleString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Helper function to get schedule type display name
export function getScheduleTypeName(type: ScheduleType): string {
  const typeNames: Record<ScheduleType, string> = {
    theory: 'Theory Session',
    practical: 'Practical Session',
    assessment: 'Assessment',
    orientation: 'Orientation',
  }
  return typeNames[type] || type
}

// Helper function to calculate schedule end time
export function getScheduleEndTime(schedule: Schedule): Date {
  const startTime = new Date(schedule.dateTime)
  return new Date(startTime.getTime() + schedule.durationMinutes * 60000)
}

// Helper to check if schedule is upcoming
export function isUpcomingSchedule(schedule: Schedule): boolean {
  return new Date(schedule.dateTime) > new Date()
}

// Helper to check if schedule is in progress
export function isScheduleInProgress(schedule: Schedule): boolean {
  const now = new Date()
  const startTime = new Date(schedule.dateTime)
  const endTime = getScheduleEndTime(schedule)
  return now >= startTime && now <= endTime
}
